import { browser } from '$app/environment';
import { page } from '$app/stores';
import { PUBLIC_BETWATCH_API_ENDPOINT } from '$env/static/public';
import type {
  BetfairMarket,
  BookmakerMarket,
  LayoutPreference,
  MarketType,
  Race,
  RacesResponse,
  Runner
} from '$types/races';
import { get, writable, type Writable } from 'svelte/store';
import type { Bookmaker } from './bookmakers';
import type { RaceFilters } from './filters';
import { activeSiteConfig } from './ui';

export class MarketMap extends Map<
  string,
  Writable<BookmakerMarket | BetfairMarket>
> {
  constructor() {
    super();
  }

  getBookmakerMarket(key: string) {
    return this.get(key) as Writable<BookmakerMarket> | undefined;
  }

  getBetfairMarket(key: string) {
    return this.get(key) as Writable<BetfairMarket> | undefined;
  }

  getBookmarketMarketFromRunner(runner: Runner, bookmaker: string | Bookmaker) {
    const race =
      browser && page ? get(page).data?.streamed?.raceData : undefined;
    if (!race) return undefined;

    //   get the marketId from the runner and bookmaker
    const market = race.data?.race.runners
      .find((r) => {
        if (r.id === runner.id) {
          return r.bookmakerMarkets?.find((bm) => bm.bookmaker === bookmaker)
            ?.id;
        }
      })
      ?.bookmakerMarkets?.find((bm) => bm.bookmaker === bookmaker)?.id;
    if (!market) return undefined;
    return this.getBookmakerMarket(market);
  }
}

export enum AustralianStates {
  'QLD',
  'NSW',
  'VIC',
  'SA',
  'WA',
  'TAS',
  'NT',
  'ACT'
}

export function isRaceClosed(race: Race) {
  return 'Open' !== race?.status;
}

export function raceTypeAbbreviation(r: Race | string) {
  // if r is of type Race, then pull out the meeting type
  let d: string;
  if (typeof r === 'string') {
    d = r;
  } else {
    d = r?.meeting?.type ?? '';
  }

  switch (d) {
    case 'Greyhound':
      return 'G';
    case 'Thoroughbred':
      return 'R';
    case 'Harness':
      return 'H';
    default:
      return '?';
  }
}

export function raceTypeBadgeClass(meeting: Race['meeting']) {
  if (meeting.type === 'Greyhound') {
    return 'bg-blue-100 text-blue-900 ring-blue-200/60';
  } else if (meeting.type === 'Harness') {
    return 'bg-green-100 text-green-900 ring-green-200/50';
  } else {
    return 'bg-orange-100 text-orange-900 ring-orange-200/50';
  }
}

export function raceStatusBadgeClass(
  race: Race,
  openGreen = false,
  closeToJump = false
) {
  if (race.status === 'Closed') {
    return 'bg-gradient-to-bl from-yellow-100 to-yellow-200 ring-yellow-200/50';
  } else if (race.status === 'Interim') {
    return 'bg-gradient-to-bl from-orange-100 to-orange-200 ring-orange-200/50';
  } else if (isRaceClosed(race)) {
    return 'bg-gradient-to-bl from-red-100 to-red-200 ring-red-200/50';
  } else if (openGreen) {
    return 'bg-gradient-to-bl from-green-100 to-green-200 ring-green-200/50';
  } else {
    if (closeToJump) {
      return 'animate-pulse bg-gradient-to-bl from-yellow-50 to-yellow-100 ring-slate-200/50 font-semibold';
    }
    return 'bg-gradient-to-bl from-slate-100 to-slate-200 ring-slate-200/50';
  }
}

// find the numbers that are missing in a list of runners up to the max number
export function getVacantBoxes(race: Race) {
  const max = Math.max(...race.runners.map((r) => r.number));
  const missingNumbers = [];
  for (let i = 1; i <= max; i++) {
    if (!race.runners.find((r) => r.number === i)) {
      missingNumbers.push(i);
    }
  }
  return missingNumbers;
}

export function isVacantBox(runner: Runner) {
  if (runner.name === 'Vacant Box') return true;
  return false;
}

export function sortRunnersByPrice(
  a: Runner,
  b: Runner,
  bookmaker: Bookmaker | null,
  bookmakerIds: { marketId: string; runnerId: string }[],
  marketMapping: MarketMap,
  marketType: MarketType,
  sortDesc: boolean
) {
  // we always sort scratched runners to the bottom with price sort
  if (a.scratchedTime) {
    return 1;
  } else if (b.scratchedTime) {
    return -1;
  }
  // sort the runners by the price of the given bookmaker
  const aMarketId = bookmakerIds.find((m) => m.runnerId === a.id)?.marketId;
  const bMarketId = bookmakerIds.find((m) => m.runnerId === b.id)?.marketId;
  if (!aMarketId) return 1;
  if (!bMarketId) return -1;

  const aMarketStore =
    bookmaker === 'Betfair'
      ? marketMapping.getBetfairMarket(aMarketId)
      : marketMapping.getBookmakerMarket(aMarketId);
  const bMarketStore =
    bookmaker === 'Betfair'
      ? marketMapping.getBetfairMarket(bMarketId)
      : marketMapping.getBookmakerMarket(bMarketId);

  if (!aMarketStore) return 1;
  if (!bMarketStore) return -1;

  if (bookmaker === 'Betfair') {
    const aMarket = get(aMarketStore as Writable<BetfairMarket>);
    const bMarket = get(bMarketStore as Writable<BetfairMarket>);
    if (!aMarket) return 1;
    if (!bMarket) return -1;

    // check if there is an sp
    if (aMarket.sp || bMarket.sp) {
      if (aMarket.sp && bMarket.sp) {
        if (!sortDesc) return aMarket.sp - bMarket.sp;
        else return bMarket.sp - aMarket.sp;
      } else if (aMarket.sp) {
        return -1;
      } else {
        return 1;
      }
    }
    if (!sortDesc)
      return (aMarket.lay?.[0]?.price ?? 0) - (bMarket.lay?.[0]?.price ?? 0);
    else return (bMarket.lay?.[0]?.price ?? 0) - (aMarket.lay?.[0]?.price ?? 0);
  } else {
    const aMarket = get(aMarketStore as Writable<BookmakerMarket>)?.[
      marketType
    ];
    const bMarket = get(bMarketStore as Writable<BookmakerMarket>)?.[
      marketType
    ];

    // if neither market, sort by number
    if (!aMarket && !bMarket) return a.number - b.number;

    if (!aMarket) return 1;
    if (!bMarket) return -1;
    if (!sortDesc) return aMarket.price - bMarket.price;
    else return bMarket.price - aMarket.price;
  }
}

export const selectedRaceHasSP = writable(false);
export const checkHasSP = (race: Race) => {
  // see if any betfair market on any runner has an sp price
  for (const runner of race.runners ?? []) {
    if (runner.betfairMarkets?.find((m) => m?.sp && m?.sp > 0)) {
      return true;
    }
  }
  return false;
};

export function bookmakerOverroundStore(
  market: BookmakerMarket,
  marketType: MarketType
) {
  const { set, subscribe } = writable(0);

  //   keep track of only unique prices
  const prices = new Map<string, number>();

  prices.set(market.id, market[marketType]?.price);
  calculate();

  function clear() {
    prices.clear();
  }

  function addMarket(market: BookmakerMarket, marketType: MarketType) {
    const price = market[marketType]?.price;
    if (price) {
      prices.set(market.id, price);
      calculate();
      return true;
    }
    return false;
  }

  function calculate() {
    let sum = 0;
    let count = 0;
    prices.forEach((price) => {
      if (!price) return;
      sum += 1 / price;
      count = count++;
    });

    set(Math.round(sum * 100));
  }

  function size() {
    return prices.size;
  }

  function sizeNotZero() {
    let count = 0;
    prices.forEach((price) => {
      if (price) count++;
    });
    return count;
  }

  function hasPrices() {
    // check if there are any nonzero prices
    for (const price of prices.values()) {
      if (price) return true;
    }
    return false;
  }

  return {
    subscribe,
    clear,
    addMarket,
    size,
    sizeNotZero,
    hasPrices
  };
}

export function getBookmakerMarketTypeFromLayoutPreference(
  layoutPreference: LayoutPreference
): 'fixedWin' | 'fixedPlace' | undefined {
  // returns either 'fixedWin' or 'fixedPlace' or undefined
  if (
    layoutPreference.type === 'BEST_PRICE_WIN' ||
    layoutPreference.type === 'FIXED_WIN' ||
    layoutPreference.type === 'FIXED_WIN_FLUCS'
  ) {
    return 'fixedWin';
  } else if (
    layoutPreference.type === 'BEST_PRICE_PLACE' ||
    layoutPreference.type === 'FIXED_PLACE' ||
    layoutPreference.type === 'FIXED_PLACE_FLUCS'
  ) {
    return 'fixedPlace';
  }
  return undefined;
}

export function getBetfairMarketTypeFromLayoutPreference(
  layoutPreference: LayoutPreference
) {
  // returns either 'win' or 'place' or undefined
  if (
    layoutPreference.type === 'BEST_PRICE_WIN' ||
    layoutPreference.type === 'FIXED_WIN' ||
    layoutPreference.type === 'FIXED_WIN_FLUCS'
  ) {
    return 'win';
  } else if (
    layoutPreference.type === 'BEST_PRICE_PLACE' ||
    layoutPreference.type === 'FIXED_PLACE' ||
    layoutPreference.type === 'FIXED_PLACE_FLUCS'
  ) {
    return 'place';
  }
  return undefined;
}

export function filterRace(
  filter: string,
  raceFilters: RaceFilters,
  race: Race,
  isFiltering: boolean
) {
  if (!race) return false;

  // if (isFiltering) {
  //   console.log('filtering');
  // }

  // always show filtered races
  if (raceFilters.ids.length > 0 && !isFiltering) {
    if (!raceFilters.ids.includes(race.id)) {
      return false;
    }
  }

  //   if we want to hide abandoned races
  if (race.status === 'Abandoned' && get(activeSiteConfig).hideAbandonedRaces)
    return false;

  // if (!filter) return true;
  // Filter based on race type
  if (raceFilters.types.length > 0) {
    const typeMap = {
      ['Harness']: 'H',
      ['Greyhound']: 'G',
      ['Thoroughbred']: 'T'
    };
    if (!race.meeting?.type) return false;
    if (!raceFilters.types.includes(typeMap[race.meeting.type])) {
      return false;
    }
  }

  if ((raceFilters.locations?.length ?? 0) > 0) {
    if (raceFilters.locations?.includes('ANZ')) {
      if (Object.values(AustralianStates).includes(race.meeting.location))
        return false;
      if (race.meeting.location === 'NZL') return false;
    }
    if (raceFilters.locations?.includes('International')) {
      if (
        !Object.values(AustralianStates).includes(race.meeting.location) &&
        race.meeting.location !== 'NZL'
      )
        return false;
    }
  }

  // Filter based on date
  // Filter based on race type
  if (raceFilters.dates.length > 0) {
    if (!race.meeting?.date) return false;
    // get timestamp for date
    if (!raceFilters.dates.includes(race.meeting.date)) {
      return false;
    }
  }
  // Filter based on meeting filter
  if (raceFilters.meetings.length > 0) {
    const key = `${race.meeting?.type}-${race.meeting?.track}-${race.meeting.location}`;
    if (!raceFilters.meetings.includes(key ?? '')) {
      return false;
    }
  }
  // Query based on filter query text
  // let filterQuery = filter.query.toLowerCase();
  // let query = get(filterQuery);
  const query = filter.replace(/[^\w ]/g, '').toLowerCase(); // remove all special characters

  if (
    (race?.meeting?.track ?? '')
      .replace(/[^\w ]/g, '')
      .toLowerCase()
      .includes(query)
  )
    return true;
  if (
    race?.name
      ?.replace(/[^\w ]/g, '')
      .toLowerCase()
      .includes(query)
  )
    return true;
  for (const runner of race?.runners ?? []) {
    if (
      runner?.name
        ?.replace(/[^\w ]/g, '')
        .toLowerCase()
        .includes(query)
    )
      return true;
    if (
      runner?.trainerName
        ?.replace(/[^\w ]/g, '')
        .toLowerCase()
        .includes(query)
    )
      return true;
    if (
      runner?.riderName
        ?.replace(/[^\w ]/g, '')
        .toLowerCase()
        .includes(query)
    )
      return true;
  }

  return false;
}

export async function getRaces(
  f: typeof fetch,
  auth: { accessToken: string | undefined; guestId: string | undefined },
  dateFrom: string | Date,
  dateTo: string | Date,
  optional?: { andDates?: string[] },
  { retriesLeft = 5, retryDelay = 1000 } = {}
) {
  const headers = {
    'Content-Type': 'application/json'
  } as Record<string, string>;
  if (auth.accessToken) {
    headers['Authorization'] = `Bearer ${auth.accessToken}`;
  } else if (auth.guestId) {
    headers['X-Guest-Id'] = auth.guestId;
  } else {
    throw new Error('No auth provided');
  }

  // Request gzip, deflate, br
  // headers['Accept-Encoding'] = 'gzip, deflate, br';

  const races = (await f(PUBLIC_BETWATCH_API_ENDPOINT, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      query: `
          query Races($dateFrom: String!, $dateTo: String!, $andDates: [String!]) {
              races(dateFrom: $dateFrom, dateTo: $dateTo, andDates: $andDates) {
                  id
                  meeting {
                      id
                      location
                      track
                      type
                      date
                  }
                  name
                  number
                  status
                  results
                  distance
                  startTime
                  actualStartTime
                  runners {
                      id
                      name
                      number
                      barrier
                      trainerName
                      riderName
                      scratchedTime
                  }
              }
          }
        `,
      variables: {
        dateFrom,
        dateTo,
        andDates: optional?.andDates
      }
    })
  })
    .then((resp) => resp.json())
    .catch(async (err) => {
      if (retriesLeft === 0) {
        throw err;
      }
      await new Promise((resolve) => setTimeout(resolve, retryDelay));
      return getRaces(f, auth, dateFrom, dateTo, optional, {
        retriesLeft: retriesLeft - 1,
        retryDelay: retryDelay * 2
      });
    })) as RacesResponse;

  if (!races?.data) {
    console.log(races?.errors);
    return races;
  }

  // parse the response as JSON and format all date strings to Date objects
  races.data?.races.forEach((r) => {
    if (r?.startTime) r.startTime = new Date(r.startTime);
  });
  return races;
}
